import {
  AgentService,
  AuthMethod,
  AuthParams,
  PresenceParams,
} from './Agent.service';
import { Dispatch, SetStateAction } from 'react';

enum BehaviorClass {
  Default = 'Default',
  OnDetect = 'OnDetect',
}

export class Presence extends AgentService {
  protected behaviorClass: BehaviorClass = BehaviorClass.Default;
  protected presence: PresenceParams;
  static readonly presenceAuthorizedEvent = 'presenceAuthorized';

  constructor(auth: AuthParams, presence: PresenceParams) {
    super(auth);
    this.presence = presence;

    this.behavior();
  }

  protected logger(event: string, data: any) {
    console.log(this.behaviorClass, event, data);
  }
  
  setLoginKey(loginKey: string) {
    this.loginKeyParams = {key: loginKey, expiration: this.getExpiration(loginKey)};
  }


  async connect() {
    this.registerEventsHandler((eventName, eventData) => {
      console.info('REPORTING-LIVE-EVENT: ', {
        eventName: eventName,
        eventData: eventData,
      });
    });
    const authMethod = this.auth.method?.toUpperCase();
    console.log(`Auth method: ${authMethod}.`);
    switch (authMethod) {
      case AuthMethod.Loginkey:
        this.handleEvents();
        await this.authWithLoginkey(this.auth);
        break;
      case AuthMethod.Saml:
        await this.authWithSaml(this.auth.groupId.toString());
        break;
      case AuthMethod.SamlWithLoginkey:
        this.authWithSamlLoginKey(this.auth.groupId.toString());
        break;
      case AuthMethod.SamlPopup:
        this.authWithSamlPopup(this.auth.groupId.toString(), this.auth.puid);
        break;
      default:
        break;
    }
    await this.connectPresence(this.presence);
  }

  async lookupVisitor() {
    this.connectPresence(this.presence);
  }

  async disconnect() {
    await this.disconnectPresence();
    this.removeBehavior();
  }

  protected behavior() {
    this.Agent.addEventListener(Presence.presenceAuthorizedEvent, (data) => {
      this.logger(Presence.presenceAuthorizedEvent, data);
    });
  }

  protected removeBehavior() {
    this.Agent.removeEventListener(Presence.presenceAuthorizedEvent, (data) => {
      this.logger(Presence.presenceAuthorizedEvent, data);
    });
  }

  public updateVisitorId(visitorId: string) {
    this.Agent.disconnectVisitor();
    this.presence.visitorId = visitorId;
    this.Agent.setPresenceVisitorId(visitorId);
  }
}

export class PresenceDetect extends Presence {
  protected connected = {
    prevState: false,
    curState: false,
  };

  static readonly visitorConnectedEvent = 'visitorConnected';
  static readonly presenceEvent = 'presence';
  static readonly presenceSessionStartEvent = 'presenceSessionStart';
  static readonly presenceSessionEndEvent = 'presenceSessionEnd';
  static readonly visitorSockEvent = 'visitorSock';
  static readonly lookupFoundEvent = 'lookupFound';
  static readonly viewerOpenedEvent = 'viewerOpened';
  // static readonly viewerClosedEvent = 'viewerClosed'; // not implemented properly / bug

  protected inSession = false;
  protected _sessionId: number = 0;

  constructor(auth: AuthParams, presence: PresenceParams) {
    super(auth, presence);
    this.behaviorClass = BehaviorClass.OnDetect;
  }

  public get sessionId() {
    return this._sessionId;
  }

  public resetSessionId() {
    this._sessionId = 0;
  }

  protected logger(event: string, data: any) {
    if (
      this.presence.debug &&
      this.connected.prevState !== this.connected.curState
    )
      console.log(this.behaviorClass, event, data);
  }

  private updateState(newState: boolean) {
    this.connected.prevState = this.connected.curState;
    this.connected.curState = newState;
  }

  private alert() {
    if (this.presence.presenceCallback) {
      if (this.connected.prevState !== this.connected.curState) {
        this.presence.presenceCallback(this.connected.curState);
      }
    }
  }

  protected behavior() {
    this.Agent.addEventListener(Presence.presenceAuthorizedEvent, (data) => {
      this.presence.permissionsCallback();
      this.logger(Presence.presenceAuthorizedEvent, data);
    });

    this.Agent.addEventListener(
      PresenceDetect.visitorConnectedEvent,
      (data) => {
        this.updateState(data.connected);
        this.alert();
        this.logger(PresenceDetect.visitorConnectedEvent, data);
      }
    );

    this.Agent.addEventListener(PresenceDetect.presenceEvent, (data) => {
      this.updateState(true);
      this.alert();
      this.logger(PresenceDetect.presenceEvent, data);
    });

    this.Agent.addEventListener(
      PresenceDetect.presenceSessionStartEvent,
      (data) => {
        if (!this.inSession) {
          this.inSession = true;
          this.presence.startSessionCallback(this.presence.visitorId);
          this.logger(PresenceDetect.presenceSessionStartEvent, data);
        }
      }
    );

    this.Agent.addEventListener(
      PresenceDetect.presenceSessionEndEvent,
      (data) => {
        this.inSession = false;
        this.presence.endSessionCallback(data);
        this.logger(PresenceDetect.presenceSessionEndEvent, data);
      }
    );

    this.Agent.addEventListener(PresenceDetect.lookupFoundEvent, (data) => {
      this._sessionId = data.id;
      this.logger(PresenceDetect.lookupFoundEvent, data);
    });

    [PresenceDetect.visitorSockEvent, PresenceDetect.viewerOpenedEvent].forEach(
      (eventName) => {
        this.Agent.addEventListener(eventName, (data) => {
          this.logger(eventName, data);
        });
      }
    );
  }

  protected removeBehavior() {
    const removeSuffix = 'Removed';
    [
      PresenceDetect.visitorConnectedEvent,
      PresenceDetect.presenceEvent,
      PresenceDetect.presenceSessionStartEvent,
      PresenceDetect.presenceSessionEndEvent,
      PresenceDetect.visitorSockEvent,
      PresenceDetect.lookupFoundEvent,
      PresenceDetect.viewerOpenedEvent,
    ].forEach((eventName) => {
      this.Agent.removeEventListener(eventName, (data) => {
        this.logger(`${eventName}${removeSuffix}`, data);
      });
    });
  }
}
